import {
  generateFilePreviewUrl,
  isImageFile,
} from "@/lib/utils/filePreview";
import { filterApplicationNote } from "../../utils/notesTabHelpers";
import type { NoteFilterType, NotesAttachment, NoteRow, NoteFile } from "../../types/tabs";
import {
  ADDITIONAL_FILES_RECEIVED_TYPE,
  NOTE_ATTACHMENT_FILE_TYPE,
  NOTE_ATTACHMENT_MODULE,
  APPLICATION_DEFAULT_USER_NOTE_FILTER_ROLES,
} from "../../constants";

export {
  ADDITIONAL_FILES_RECEIVED_TYPE,
  NOTE_ATTACHMENT_FILE_TYPE,
  NOTE_ATTACHMENT_MODULE,
} from "../../constants";
export type { EditableNote } from "../../types/utils";

export function resolveNoteAttachmentIds(
  attachment: NotesAttachment,
): { fileId: string; refId: string } | null {
  const fileId = attachment.id ?? attachment.attachment_id;
  const refId =
    attachment.fk_ref_id ?? attachment.fk_refid ?? attachment.fk_refId;
  if (
    fileId == null ||
    refId == null ||
    String(fileId) === "" ||
    String(refId) === ""
  ) {
    return null;
  }
  return { fileId: String(fileId), refId: String(refId) };
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message || fallback;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}

export function isAccessDeniedMessage(message: string): boolean {
  return message.trim().toLowerCase() === "access denied";
}

/** Angular `note-list.loadNotes`: Underwriter / CS roles default to User Notes on APPLICATION. */
export function getDefaultApplicationNoteFilter(roleName: string): NoteFilterType {
  if ((APPLICATION_DEFAULT_USER_NOTE_FILTER_ROLES as readonly string[]).includes(roleName)) {
    return "user";
  }
  return "all";
}

export function filterVisibleNotes(
  notes: NoteRow[],
  noteFilter: NoteFilterType,
  isAdmin: boolean,
): NoteRow[] {
  return notes.filter((note) => {
    if (!isAdmin && note.notes?.includes("ITR Ratio:")) {
      return false;
    }
    return filterApplicationNote(note, noteFilter);
  });
}

export function buildAdditionalImageFilesList(
  files: NoteFile[] | undefined,
  applicantId: string | number,
): string[] | undefined {
  const list = (Array.isArray(files) ? files : [])
    .filter(
      (file: NoteFile) =>
        file?.attachment_id != null &&
        isImageFile(file.filename || file.name || file.filepath || ""),
    )
    .map((file: NoteFile) =>
      generateFilePreviewUrl(
        file.attachment_id ?? "",
        ADDITIONAL_FILES_RECEIVED_TYPE,
        applicantId,
        NOTE_ATTACHMENT_MODULE,
      ),
    );
  return list.length > 1 ? list : undefined;
}

export function buildNoteImageFilesList(
  attachments: NotesAttachment[] = [],
): string[] | undefined {
  const list = attachments
    .map((attachment) => {
      const ids = resolveNoteAttachmentIds(attachment);
      if (!ids || !isImageFile(attachment.attachment_name || "")) return null;
      return generateFilePreviewUrl(
        ids.fileId,
        NOTE_ATTACHMENT_FILE_TYPE,
        ids.refId,
        NOTE_ATTACHMENT_MODULE,
      );
    })
    .filter((url): url is string => url !== null);
  return list.length > 1 ? list : undefined;
}
